import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import API_BASE from '../config';
import CatalogService from '../services/catalog.service';
import HtmlEditor from './HtmlEditor';
import Pagination from './Pagination';
import ProductService from '../services/product.service';
import SubCatalogService from '../services/subcatalog.service';
import UploadService from '../services/upload.service';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../common/utils';
import Spinner from './Spinner';

// Resolve stored image_url (full URL or "uploads/img/products/x.jpg") to a browser src.
const resolveImg = (val) => {
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  const clean = val.replace(/^uploads\//, '/');
  return API_BASE + (clean.startsWith('/') ? clean : '/' + clean);
};

const galleryThumb = { width: '100%', height: 130, objectFit: 'cover', borderRadius: 6 };

const emptyForm = {
  name: '',
  price: '',
  description: '',
  image_url: '',
  catalog_id: '',
  sub_catalog_id: '',
};

const Products = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [subCatalogs, setSubCatalogs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const PAGE_SIZE = 10;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  // Params currently applied to the list (kept so pagination preserves the search).
  const [appliedSearch, setAppliedSearch] = useState({ q: '', category: '' });

  // Extra (gallery) images — same UX as the album form. The cover stays in form.image_url.
  // existingImages: persisted rows { id, url, caption, _remove }
  // newImages:      local picks { file, preview, caption }
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const galleryInput = useRef(null);
  const newImagesRef = useRef(newImages);
  newImagesRef.current = newImages;

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message || t('product.errors.generic');

  const load = (p = page, search = appliedSearch) => {
    setLoading(true);
    const params = { page: p, size: PAGE_SIZE };
    if (search.q) params.q = search.q;
    if (search.category) params.category = search.category;
    ProductService.getAll(params)
      .then((res) => {
        setProducts(res.data.items);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.totalItems || 0);
        setPage(res.data.currentPage || p);
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setLoading(false));
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    load(p);
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const next = { q: searchName.trim(), category: searchCategory.trim() };
    setAppliedSearch(next);
    load(1, next);
  };

  const clearSearch = () => {
    setSearchName('');
    setSearchCategory('');
    const next = { q: '', category: '' };
    setAppliedSearch(next);
    load(1, next);
  };

  const loadRefs = () => {
    CatalogService.getAll()
      .then((res) => setCatalogs(res.data))
      .catch((err) => toast.error(resolveMsg(err)));
    SubCatalogService.getAll()
      .then((res) => setSubCatalogs(res.data))
      .catch((err) => toast.error(resolveMsg(err)));
  };

  useEffect(() => {
    loadRefs();
    load();
    // Revoke preview object URLs on unmount.
    return () => newImagesRef.current.forEach((f) => URL.revokeObjectURL(f.preview));
  }, []);

  // ---- Extra images (gallery) ----
  const MAX_GALLERY_FILE = 2 * 1024 * 1024;
  const addGalleryFiles = (fileList) => {
    const all = Array.from(fileList || []);
    const picked = all.filter((f) => f.type.startsWith('image/') && f.size <= MAX_GALLERY_FILE);
    if (picked.length < all.length) toast.error(t('product.errors.someImagesSkipped', 'Some files were skipped (not an image or larger than 2 MB).'));
    if (picked.length === 0) return;
    setNewImages((prev) => [...prev, ...picked.map((file) => ({ file, preview: URL.createObjectURL(file), caption: '' }))]);
  };

  const handleGallerySelect = (e) => {
    addGalleryFiles(e.target.files);
    e.target.value = '';
  };

  const handleGalleryDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addGalleryFiles(e.dataTransfer.files);
  };

  const removeNewImage = (idx) =>
    setNewImages((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });

  const setNewImageCaption = (idx, caption) => setNewImages((prev) => prev.map((f, i) => (i === idx ? { ...f, caption } : f)));

  const toggleRemoveExisting = (imgId) => setExistingImages((prev) => prev.map((img) => (img.id === imgId ? { ...img, _remove: !img._remove } : img)));

  const setExistingCaption = (imgId, caption) => setExistingImages((prev) => prev.map((img) => (img.id === imgId ? { ...img, caption } : img)));

  const moveExisting = (idx, dir) =>
    setExistingImages((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  const clearGallery = () => {
    newImagesRef.current.forEach((f) => URL.revokeObjectURL(f.preview));
    setNewImages([]);
    setExistingImages([]);
  };

  // Sub-catalogs available for the selected catalog.
  const filteredSubCatalogs = useMemo(() => {
    if (!form.catalog_id) return [];
    return subCatalogs.filter((s) => String(s.catalog_id) === String(form.catalog_id));
  }, [form.catalog_id, subCatalogs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'catalog_id') {
      // Reset sub-catalog when catalog changes.
      setForm((f) => ({ ...f, catalog_id: value, sub_catalog_id: '' }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    clearGallery();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');

    if (!form.name.trim()) {
      toast.error(t('product.errors.nameRequired'));
      return;
    }
    if (!form.catalog_id) {
      toast.error(t('product.errors.catalogRequired'));
      return;
    }
    if (!form.sub_catalog_id) {
      toast.error(t('product.errors.subCatalogRequired'));
      return;
    }

    const kept = existingImages.filter((img) => !img._remove);
    const payload = {
      ...form,
      price: form.price === '' ? 0 : Number(form.price),
      // Extra images meta (ignored on create when empty).
      order: kept.map((img) => img.id),
      removeImageIds: existingImages.filter((img) => img._remove).map((img) => img.id),
      captions: kept.reduce((acc, img) => {
        acc[img.id] = img.caption || '';
        return acc;
      }, {}),
      newCaptions: newImages.map((f) => f.caption || ''),
    };
    const files = newImages.map((f) => f.file);

    setSaving(true);
    setSaveProgress(0);
    const action = editingId ? ProductService.update(editingId, payload, files, setSaveProgress) : ProductService.create(payload, files, setSaveProgress);

    action
      .then(() => {
        setMessage(editingId ? t('product.updated') : t('product.created'));
        resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setSaving(false));
  };

  const handleEdit = (p) => {
    clearGallery();
    setEditingId(p.id);
    // List rows don't include images; fetch the full product for its gallery.
    ProductService.get(p.id)
      .then((res) => setExistingImages((res.data.images || []).map((img) => ({ ...img, _remove: false }))))
      .catch((err) => toast.error(resolveMsg(err)));
    setForm({
      name: p.name || '',
      price: p.price != null ? p.price : '',
      description: p.description || '',
      image_url: p.image_url || '',
      catalog_id: p.catalog_id || '',
      sub_catalog_id: p.sub_catalog_id || '',
    });
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('product.confirmDelete'))) return;
    setMessage('');
    ProductService.remove(id)
      .then(() => {
        setMessage(t('product.deleted'));
        if (editingId === id) resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  const nameOf = (list, id, embedded) => (embedded && embedded.name) || (list.find((x) => x.id === id) || {}).name || id;

  const handleImageUpload = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setMessage('');

    if (!f.type.startsWith('image/')) {
      toast.error(t('product.errors.selectImage'));
      e.target.value = '';
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error(t('product.errors.imageTooLarge'));
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    UploadService.uploadImage(f, setUploadProgress)
      .then((res) => {
        // Auto-fill image_url with the uploaded file's stored path.
        setForm((prev) => ({ ...prev, image_url: res.data.image_url }));
        setMessage(t('product.imageUploaded'));
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => {
        setUploading(false);
        e.target.value = '';
      });
  };

  return (
    <div className='container mt-4'>
      <h3>{editingId ? t('product.editTitle') : t('product.createTitle')}</h3>

      {message && <div className='alert alert-success'>{message}</div>}

      <form onSubmit={handleSubmit} className='card card-body mb-4'>
        <div className='row'>
          <div className='col-md-6 mb-3'>
            <label className='form-label'>{t('product.catalogRequired')}</label>
            <select className='form-select' name='catalog_id' value={form.catalog_id} onChange={handleChange}>
              <option value=''>{t('product.selectCatalog')}</option>
              {catalogs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-md-6 mb-3'>
            <label className='form-label'>{t('product.subCatalogRequired')}</label>
            <select className='form-select' name='sub_catalog_id' value={form.sub_catalog_id} onChange={handleChange} disabled={!form.catalog_id}>
              <option value=''>{form.catalog_id ? t('product.selectSubCatalog') : t('product.selectCatalogFirst')}</option>
              {filteredSubCatalogs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='row'>
          <div className='col-md-8 mb-3'>
            <label className='form-label'>{t('product.nameRequired')}</label>
            <input type='text' className='form-control' name='name' value={form.name} onChange={handleChange} placeholder={t('product.namePlaceholder')} />
          </div>
          <div className='col-md-4 mb-3'>
            <label className='form-label'>{t('common.price')}</label>
            <input type='number' step='0.01' min='0' className='form-control' name='price' value={form.price} onChange={handleChange} placeholder={t('product.pricePlaceholder')} />
          </div>
        </div>

        <div className='mb-3'>
          <label className='form-label'>{t('common.description')}</label>
          <HtmlEditor value={form.description} onChange={(html) => setForm((f) => ({ ...f, description: html }))} rows={6} placeholder={t('product.descriptionPlaceholder')} />
          <div className='form-text'>{t('product.descriptionHtmlHint')}</div>
        </div>

        <div className='mb-3'>
          <label className='form-label'>
            {t('product.coverImage', 'Cover image (default)')} <small className='text-muted'>— {t('product.imageUrl')}</small>
          </label>
          <input type='text' className='form-control' name='image_url' value={form.image_url} onChange={handleChange} placeholder={t('product.imageUrlPlaceholder')} />
          <div className='mt-2'>
            <label className='form-label d-block small text-muted mb-1'>{t('product.uploadHint')}</label>
            <input type='file' className='form-control' accept='image/*' onChange={handleImageUpload} disabled={uploading} />
          </div>
          {uploading && (
            <div className='progress mt-2'>
              <div className='progress-bar' role='progressbar' style={{ width: `${uploadProgress}%` }} aria-valuenow={uploadProgress} aria-valuemin='0' aria-valuemax='100'>
                {uploadProgress}%
              </div>
            </div>
          )}
          {form.image_url && (
            <img
              src={resolveImg(form.image_url)}
              alt='preview'
              className='mt-2'
              style={{ maxHeight: 80, borderRadius: 4 }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </div>

        <div className='mb-3'>
          <label className='form-label'>{t('product.galleryImages', 'More images')}</label>
          <div
            className={`border rounded p-4 text-center mb-3 ${dragOver ? 'border-primary bg-light' : 'border-2'}`}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => galleryInput.current && galleryInput.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleGalleryDrop}
          >
            <div className='fw-semibold'>{t('album.dropHere', 'Drop images here or click to upload')}</div>
            <div className='small text-muted'>{t('product.dropHint', 'You can select multiple images (jpg, png, gif, webp — max 2MB each)')}</div>
            <input ref={galleryInput} type='file' accept='image/*' multiple hidden onChange={handleGallerySelect} />
          </div>

          {existingImages.length > 0 && (
            <>
              <h6 className='text-muted'>{t('album.currentImages', 'Current images')}</h6>
              <div className='row g-3 mb-3'>
                {existingImages.map((img, idx) => (
                  <div className='col-6 col-md-3' key={img.id}>
                    <div className={`card h-100 ${img._remove ? 'opacity-50 border-danger' : ''}`}>
                      <img src={API_BASE + img.url} alt={img.caption || ''} style={galleryThumb} />
                      <div className='card-body p-2'>
                        <input
                          type='text'
                          className='form-control form-control-sm mb-2'
                          placeholder={t('album.caption', 'Caption')}
                          value={img.caption || ''}
                          onChange={(e) => setExistingCaption(img.id, e.target.value)}
                          disabled={img._remove}
                        />
                        <div className='d-flex justify-content-between'>
                          <div className='btn-group btn-group-sm'>
                            <button type='button' className='btn btn-outline-secondary' onClick={() => moveExisting(idx, -1)} disabled={idx === 0 || img._remove} title={t('album.moveLeft', 'Move left')}>
                              ←
                            </button>
                            <button
                              type='button'
                              className='btn btn-outline-secondary'
                              onClick={() => moveExisting(idx, 1)}
                              disabled={idx === existingImages.length - 1 || img._remove}
                              title={t('album.moveRight', 'Move right')}
                            >
                              →
                            </button>
                          </div>
                          <button type='button' className={`btn btn-sm ${img._remove ? 'btn-secondary' : 'btn-outline-danger'}`} onClick={() => toggleRemoveExisting(img.id)}>
                            {img._remove ? t('common.undo', 'Undo') : t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {newImages.length > 0 && (
            <>
              <h6 className='text-muted'>{t('album.newImages', 'New images to upload')}</h6>
              <div className='row g-3 mb-3'>
                {newImages.map((f, idx) => (
                  <div className='col-6 col-md-3' key={f.preview}>
                    <div className='card h-100'>
                      <img src={f.preview} alt='' style={galleryThumb} />
                      <div className='card-body p-2'>
                        <input type='text' className='form-control form-control-sm mb-2' placeholder={t('album.caption', 'Caption')} value={f.caption} onChange={(e) => setNewImageCaption(idx, e.target.value)} />
                        <button type='button' className='btn btn-sm btn-outline-danger w-100' onClick={() => removeNewImage(idx)}>
                          {t('common.remove', 'Remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {saving && saveProgress > 0 && (
          <div className='progress mb-3' style={{ height: 6 }}>
            <div className='progress-bar' role='progressbar' style={{ width: `${saveProgress}%` }} />
          </div>
        )}

        <div className='d-flex gap-2'>
          <button type='submit' className='btn btn-primary' disabled={saving || uploading}>
            {saving ? t('common.saving', 'Saving…') : editingId ? t('common.update') : t('common.create')}
          </button>
          {editingId && (
            <button type='button' className='btn btn-secondary' onClick={resetForm}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>

      <h4>
        {t('product.listTitle')} {totalItems > 0 && <small className='text-muted'>{t('product.totalCount', { count: totalItems })}</small>}
      </h4>

      <form className='row g-2 mb-3' onSubmit={handleSearch}>
        <div className='col-12 col-sm-5'>
          <input type='text' className='form-control' placeholder={t('catalog.searchNamePlaceholder')} value={searchName} onChange={(e) => setSearchName(e.target.value)} />
        </div>
        <div className='col-12 col-sm-4'>
          <input type='text' className='form-control' placeholder={t('catalog.searchCategoryPlaceholder')} value={searchCategory} onChange={(e) => setSearchCategory(e.target.value)} />
        </div>
        <div className='col-12 col-sm-3 d-flex gap-2'>
          <button type='submit' className='btn btn-outline-primary flex-grow-1'>
            {t('catalog.searchButton')}
          </button>
          {(appliedSearch.q || appliedSearch.category) && (
            <button type='button' className='btn btn-outline-secondary' onClick={clearSearch}>
              {t('catalog.clearSearch')}
            </button>
          )}
        </div>
      </form>
      {loading ? (
        <Spinner />
      ) : products.length === 0 ? (
        <p className='text-muted'>{t('product.noProducts')}</p>
      ) : (
        <div className='table-responsive rounded'>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <th style={{ width: 50 }}>{t('common.id')}</th>
                <th style={{ width: 100 }}>{t('common.image')}</th>
                <th>{t('common.name')}</th>
                <th style={{ width: 100 }}>{t('common.price')}</th>
                <th>{t('product.catalog')}</th>
                <th>{t('product.subCatalog')}</th>
                <th style={{ width: 160 }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>
                    {p.image_url ? (
                      <img
                        src={resolveImg(p.image_url)}
                        alt={p.name}
                        style={{ maxHeight: 40, maxWidth: 60, objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </td>
                  <td>{p.name}</td>
                  <td>{formatCurrency(p.price)}</td>
                  <td>{nameOf(catalogs, p.catalog_id, p.catalog)}</td>
                  <td>{nameOf(subCatalogs, p.sub_catalog_id, p.sub_catalog)}</td>
                  <td>
                    <button className='btn btn-sm btn-outline-primary me-2' onClick={() => handleEdit(p)}>
                      {t('common.edit')}
                    </button>
                    <button className='btn btn-sm btn-outline-danger' onClick={() => handleDelete(p.id)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && <Pagination page={page} totalPages={totalPages} onChange={goToPage} />}
    </div>
  );
};

export default Products;
